import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { LiveTerminal } from '@iws/backoffice-api'

import { TerminalModel } from '@/http/models/Terminal'
import { useAuth } from '@/contexts/AuthContext'
import { useBusiness } from '@/contexts/BusinessContext'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { PageHeader } from '@/components/atoms'
import { FilterSelect } from '@/components/features/pos/FilterSelect'
import { PosListCard } from '@/components/features/pos/PosListCard'

type Filter = 'all' | 'open' | 'offline' | 'long'

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open shifts' },
  { value: 'offline', label: 'Offline' },
  { value: 'long', label: 'Shift open > 12 h' },
]

const REFRESH_MS = 30_000

const time = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
const minutesAgo = (iso: string | null) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000)) : null)

const SyncCell = ({ t }: { t: LiveTerminal }) => {
  const ago = minutesAgo(t.last_synced_at)
  const dot = t.sync_status === 'online' ? 'bg-success' : t.sync_status === 'offline' ? 'bg-warning' : 'bg-grey-500'
  const text = t.sync_status === 'online' ? 'online' : t.sync_status === 'idle' ? 'idle' : ago === null ? 'offline · never synced' : `offline · ${ago} min`
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-text-2">
      <span className={`size-2 rounded-full ${dot}`} />
      {text}
    </span>
  )
}

/**
 * Each terminal of every branch with its open shift (POS.md §18, Terminals page, design screen 2): cashier, drawer,
 * sales today and when its device last synced, grouped by branch. Refreshes every 30 seconds.
 */
export const LiveTerminals = ({ branches }: { branches: { _id: string; name: string }[] }) => {
  const { can } = useAuth()
  const { formatCurrency } = useBusiness()
  const navigate = useNavigate()
  const [rows, setRows] = useState<LiveTerminal[]>([])
  const [loading, setLoading] = useState(true)
  const [branch, setBranch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')

  const load = useCallback(async () => {
    const res = await TerminalModel.getLive(branch || undefined)
    if (res.success && res.data) setRows(res.data.result)
    setLoading(false)
  }, [branch])

  useEffect(() => {
    setLoading(true)
    load()
    const timer = window.setInterval(load, REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [load])

  const requestClose = async (t: LiveTerminal) => {
    if (!t.shift) return
    const res = await TerminalModel.requestClose(t.shift._id)
    if (!res.success) return toast.error(res.error?.data?.message ?? 'Could not ask to close the shift.')
    toast.success(`${t.name} will ask to close its shift on its next sync`)
    load()
  }

  const shown = rows
    .filter((t) =>
      filter === 'open' ? !!t.shift : filter === 'offline' ? t.sync_status === 'offline' : filter === 'long' ? !!t.shift?.long_open : true
    )
    .sort((a, b) => (a.branch?.name ?? '').localeCompare(b.branch?.name ?? '') || a.unique_no - b.unique_no)
  const openCount = rows.filter((t) => t.shift).length

  return (
    <div className="space-y-4">
      <PageHeader
        title="Terminals"
        description={`${new Set(rows.map((t) => t.branch_id)).size} branches · ${rows.length} terminals · ${openCount} open now. The open shift of each terminal, as its device last synced.`}
      />
      <PosListCard<LiveTerminal>
        rows={shown}
        loading={loading}
        empty="No terminals match."
        filters={
          <div className="flex flex-wrap items-center gap-2">
            {branches.length > 1 && (
              <FilterSelect
                label="Branch"
                value={branch}
                onChange={setBranch}
                allLabel="All branches"
                options={branches.map((b) => ({ value: b._id, label: b.name }))}
              />
            )}
            {FILTERS.map((f) => (
              <Button key={f.value} size="sm" variant={filter === f.value ? 'default' : 'outline'} onClick={() => setFilter(f.value)}>
                {f.label}
              </Button>
            ))}
          </div>
        }
        columns={[
          { label: 'Terminal', render: (t) => <span className="font-medium text-ink">T{t.unique_no} · {t.name}</span> },
          { label: 'Branch', render: (t) => t.branch?.name ?? '—' },
          { label: 'Cashier', render: (t) => t.shift?.staff?.name ?? '—', className: 'hidden md:table-cell' },
          {
            label: 'Shift',
            render: (t) =>
              !t.shift ? (
                <Badge tone="soft">Closed</Badge>
              ) : (
                <span className="inline-flex flex-wrap items-center gap-1">
                  <Badge tone={t.shift.long_open ? 'warning' : 'success'}>
                    {t.shift.long_open ? `Open since ${time(t.shift.opened_at)} (> 12 h)` : `Open ${time(t.shift.opened_at)}`}
                  </Badge>
                  {t.shift.close_requested_at && <Badge tone="warning">Close asked</Badge>}
                  {t.shift.shift_no && <span className="font-mono text-xs text-muted-foreground">{t.shift.shift_no}</span>}
                </span>
              ),
          },
          { label: 'Drawer', render: (t) => <span className="tabular-nums">{t.shift ? formatCurrency(t.shift.drawer) : '—'}</span>, className: 'text-right' },
          {
            label: 'Sales today',
            render: (t) => <span className="tabular-nums">{t.sales_today.orders ? `${formatCurrency(t.sales_today.total)} · ${t.sales_today.orders}` : '—'}</span>,
            className: 'hidden text-right lg:table-cell',
          },
          { label: 'Sync', render: (t) => <SyncCell t={t} /> },
          {
            label: '',
            className: 'text-right',
            render: (t) => (
              <span className="inline-flex gap-2">
                {t.shift && !t.shift.close_requested_at && can('pos:close_shift') && (
                  <Button size="sm" variant="outline" onClick={() => requestClose(t)}>
                    Ask to close
                  </Button>
                )}
                {can('pos:open_shift') && (
                  <Button size="sm" variant="outline" onClick={() => navigate(`/pos/shifts?terminal_id=${t._id}`)}>
                    View
                  </Button>
                )}
              </span>
            ),
          },
        ]}
      />
    </div>
  )
}
